import { Component, effect, input, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { BookingService } from '@beautyapp/core';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { TranslateModule } from '@ngx-translate/core';

interface AuditLog {
  id: string;
  action: 'CREATED' | 'UPDATED' | 'DELETED' | 'COMPLETED';
  changedBy: { fullName: string };
  timestamp: Date;
  oldValue: any;
  newValue: any;
}

interface FieldChange {
  field: string;
  oldValue: any;
  newValue: any;
}

@Component({
  selector: 'beautyapp-visit-audit-history',
  standalone: true,
  imports: [
    CommonModule,
    NzTableModule,
    NzTagModule,
    NzSpinModule,
    NzEmptyModule,
    NzButtonModule,
    NzIconModule,
    TranslateModule,
    DatePipe,
  ],
  templateUrl: './visit-audit-history.component.html',
  styleUrls: ['./visit-audit-history.component.scss'],
})
export class VisitAuditHistoryComponent implements OnInit, OnChanges {
  @Input() visitId: number | null = null;

  reloadCount = input<number>()

  auditLogs: AuditLog[] = [];
  loading = false;
  expandedRows = new Set<string>();

  fieldLabels: Record<string, string> = {
    bookingDate: 'თარიღი',
    startTime: 'დაწყება',
    endTime: 'დასრულება',
    status: 'სტატუსი',
    employeeId: 'თანამშრომელი',
    userId: 'მომხმარებელი',
    description: 'აღწერა',
    cash: 'ნაღდი',
    card: 'ბარათი',
    balance: 'ბალანსი',
    barter: 'ბარტერი',
    bankTransfer: 'საბანკო გადარიცხვა',
    consignation: 'კონსიგნაცია',
    minutes: 'წუთები',
    sendNotification: 'შეტყობინება',
    onUserRequest: 'მომხმარებლის მოთხოვნით',
    totalBookingPayment: 'ჯამური თანხა (პროცედურები)',
    totalRealizationPayment: 'ჯამური თანხა (პროდუქცია)',
    totalBookingMinutes: 'ჯამური წუთები',
    paymentType: 'გადახდის ტიპი',
  };

  constructor(private bookingService: BookingService) {
    effect(() => {
      if (this.reloadCount()){
        this.loadAuditLogs();
      }
    });
  }

  ngOnInit(): void {
    this.loadAuditLogs();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visitId'] && !changes['visitId'].firstChange) {
      this.loadAuditLogs();
    }
  }

  loadAuditLogs(): void {
    if (!this.visitId) return;

    this.loading = true;
    this.bookingService.getVisitAuditLog(this.visitId).subscribe({
      next: (res) => {
        this.auditLogs = res?.auditLogs || [];
        this.loading = false;
      },
      error: () => {
        this.auditLogs = [];
        this.loading = false;
      },
    });
  }

  toggleRow(id: string): void {
    if (this.expandedRows.has(id)) {
      this.expandedRows.delete(id);
    } else {
      this.expandedRows.add(id);
    }
  }

  isExpanded(id: string): boolean {
    return this.expandedRows.has(id);
  }

  getActionLabel(action: string): string {
    const labels: Record<string, string> = {
      CREATED: 'შექმნა',
      UPDATED: 'რედაქტირება',
      DELETED: 'წაშლა',
      COMPLETED: 'დასრულება',
    };
    return labels[action] || action;
  }

  getActionColor(action: string): string {
    const colors: Record<string, string> = {
      CREATED: 'green',
      UPDATED: 'blue',
      DELETED: 'red',
      COMPLETED: 'purple',
    };
    return colors[action] || 'default';
  }

  getSummary(log: AuditLog): string {
    if (log.action === 'CREATED') return 'ვიზიტი შეიქმნა';
    if (log.action === 'DELETED') return 'ვიზიტი წაიშალა';
    if (log.action === 'COMPLETED') return 'ვიზიტი დასრულდა';

    const changes = this.getFieldChanges(log);
    if (changes.length === 0) return 'ცვლილებები არ არის';

    const changedFields = changes.map((c) => this.fieldLabels[c.field] || c.field);
    return `შეიცვალა: ${changedFields.slice(0, 3).join(', ')}${changedFields.length > 3 ? '...' : ''}`;
  }

  getFieldChanges(log: AuditLog): FieldChange[] {
    if (!log.oldValue || !log.newValue) return [];

    const changes: FieldChange[] = [];
    const trackedFields = Object.keys(this.fieldLabels);

    for (const key of trackedFields) {
      const oldVal = log.oldValue?.[key];
      const newVal = log.newValue?.[key];

      if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
        changes.push({
          field: key,
          oldValue: oldVal,
          newValue: newVal,
        });
      }
    }

    return changes;
  }

  formatValue(value: any): string {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'დიახ' : 'არა';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }

  hasChanges(log: AuditLog): boolean {
    return log.action !== 'CREATED' && this.getFieldChanges(log).length > 0;
  }
}
